<script setup lang="ts">
// ================================
// LA CARTE DE TÊTE DU RÉSULTAT (contrat de l'interface v1.2, §4 ; maquette
// 2b jugée le 18/10/2026 ; DEV-2311, lot 4b, DEV-2419).
//
// Ce que le courtier doit vivre : trois lignes en cinq secondes. Les
// métadonnées (« PDL … (C4) · Étude du … à hh:mm · Réf. XXXXXX ») et
// « Nouvelle étude » à droite ; le verdict, dont le montant seul porte le
// signe et la couleur, « HT » et « par an » hors de la couleur (D1 b) ; la
// raison de la décision ; une fiche de trois étiquettes au plus ; une seule
// ligne orange quand une action précède l'envoi, au format court (A1), ou un
// constat gris sur « Rien à changer » ; à droite, un bouton de niveau 1, un
// de niveau 2 au plus et « Voir le résumé » en lien de niveau 3 (R-T17, A2) ;
// la ligne de périmètre. « Préparer la demande » et « Voir le résumé »
// ouvrent la rangée « Rapport et demande » (ancre #rapport, D16) : la carte
// ne copie plus rien à l'aveugle (D3), sans case d'accord ni voix (D4, D5).
//
// Tout vient de `lireCarteDeTete` (module pur) : la carte n'invente aucun
// montant, une valeur absente ne s'écrit pas.
// ================================
import { computed, onBeforeUnmount, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import {
    NIVEAU_1,
    NIVEAU_2,
    NIVEAU_3,
} from "@modules/courtage/utils/optim-turpe-v2.boutons"
import {
    libelleChiffrerSurLaPeriode,
    metadonneesDeLaCarte,
    texteDuTitre,
    type CarteDeTete,
} from "@modules/courtage/utils/optim-turpe-v2.carte"
import {
    classeMontant,
    classeSegment,
    formatMontantSigne,
    suiteColleeAuNombre,
} from "@modules/courtage/utils/optim-turpe-v2.montant"
import OptimTurpeV2TexteSigne from "./OptimTurpeV2TexteSigne.vue"
import type { AncreAffiner } from "@modules/courtage/utils/optim-turpe-v2.completer"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

const { t } = useI18n({
    locale: "fr",
    messages: {
        fr: {
            fiche: {
                scenario: "Scénario",
                aDemander: "À demander",
                dontVient: "D'où vient le montant",
                periode: "Période la plus complète",
            },
            recommande: "Recommandé",
            nouvelleEtude: "Nouvelle étude",
            surLesDernieresMesures: "Sur les dernières mesures",
            surLesDernieresMesuresAide:
                "Mêmes réglages, sur les 12 derniers mois mesurés.",
            chiffrerAutres: "Chiffrer d'autres puissances",
            chiffrerAutresAide:
                "La formule et les puissances de votre choix, chiffrées telles quelles face au contrat actuel.",
            chiffrerCettePeriode: "Chiffrer sur cette période",
            preparerDemande: "Préparer la demande",
            voirResume: "Voir le résumé",
            voirRecommandation: "Voir la recommandation",
            modifierHypotheses: "Modifier les hypothèses",
            telecharger: "Télécharger le rapport",
            corriger: "Corriger",
        },
    },
})

type Props = {
    etude: OptimTurpeV2Etude
    carte: CarteDeTete
    /** Le point de livraison, masqué à l'écran (R-T16). */
    pdl?: string | null
    /** « 05/10/2026 à 16:21 » : la date et l'heure de calcul de l'étude. */
    date?: string | null
    /** Le rapport se propose-t-il (sa route répond) ? */
    rapportProposable?: boolean
    rapportEnCours?: boolean
    /** « Nouvelle étude » se propose-t-il (étude réelle, relançable) ? */
    relancable?: boolean
}
const props = withDefaults(defineProps<Props>(), {
    pdl: null,
    date: null,
    rapportProposable: false,
    rapportEnCours: false,
    relancable: true,
})

const emit = defineEmits<{
    /** « Sur les dernières mesures ». */
    "nouvelle-etude": []
    /** « Chiffrer d'autres puissances » : ouvre la saisie des puissances. */
    "chiffrer-autres": []
    /** « Chiffrer sur cette période ». */
    "chiffrer-periode": [periode: { date_from: string; date_to: string }]
    /** « Voir la recommandation » (étude saisie). */
    "voir-recommandation": []
    /** « Voir la recommandation » sur l'autre scénario : retenir le conseil. */
    "retenir-scenario": [scenario: "sans" | "avec"]
    /** « Modifier les hypothèses » : étude non chiffrée sans période. */
    "modifier-hypotheses": []
    /** « Préparer la demande » : la rangée s'ouvre sur l'aperçu du courriel. */
    "preparer-demande": []
    /** « Voir le résumé » : la rangée s'ouvre sur le résumé pour le client. */
    "voir-resume": []
    /** « Corriger » : ouvre le champ dans « Modifier les hypothèses ». */
    corriger: [ancre: AncreAffiner]
    telecharger: []
}>()

const menuOuvert = ref(false)
/** Le bouton et son menu : un clic hors de ce bloc referme le menu (D25). */
const blocMenu = ref<HTMLElement | null>(null)

watch(
    () => props.etude,
    () => {
        menuOuvert.value = false
    }
)

/** Le menu « Nouvelle étude » se ferme par Échap et par un clic à l'extérieur. */
function fermerSiDehors(evenement: Event): void {
    const cible = evenement.target as Node | null
    if (blocMenu.value && cible && !blocMenu.value.contains(cible))
        menuOuvert.value = false
}
function fermerSurEchap(evenement: KeyboardEvent): void {
    if (evenement.key === "Escape") menuOuvert.value = false
}
watch(menuOuvert, (ouvert) => {
    if (typeof document === "undefined") return
    if (ouvert) {
        document.addEventListener("mousedown", fermerSiDehors)
        document.addEventListener("keydown", fermerSurEchap)
    } else {
        document.removeEventListener("mousedown", fermerSiDehors)
        document.removeEventListener("keydown", fermerSurEchap)
    }
})
onBeforeUnmount(() => {
    if (typeof document === "undefined") return
    document.removeEventListener("mousedown", fermerSiDehors)
    document.removeEventListener("keydown", fermerSurEchap)
})

/** « PDL 300 009 … 23 (C2) · Étude du 05/10/2026 à 16:21 · Réf. 712409 ». */
const getEnTete = computed(() =>
    metadonneesDeLaCarte(
        props.pdl,
        props.etude.compteur?.segment,
        props.date,
        props.etude._id ?? null
    )
)

/** Le montant seul du verdict, signé, sans « HT » (D1 b : hors de la couleur). */
const getMontant = computed(() =>
    props.carte.titre.montantEur === null
        ? null
        : formatMontantSigne(props.carte.titre.montantEur)
)

/** « HT par an » collé au montant par des espaces insécables (N3). */
const getUniteInsecable = computed(() =>
    suiteColleeAuNombre("HT", props.carte.titre.unite)
)

/** La fiche a-t-elle au moins une ligne ? */
const getFicheVisible = computed(() => {
    const f = props.carte.fiche
    return Boolean(
        f.scenario ||
            f.aDemander ||
            f.pourquoi ||
            f.composition.length > 0 ||
            f.provenance ||
            f.periode
    )
})

/** Le rapport se propose-t-il en niveau 2 sur la carte (A9) ? */
/**
 * Revue du 19/10/2026 (N7) : sans aucune ligne (scénario, à demander, fiche)
 * ni alerte, la colonne de gauche serait vide ; les actions viennent alors
 * sous la raison, alignées à gauche, sans filet ni colonne vide.
 */
const getSansLigne = computed(
    () => !getFicheVisible.value && !props.carte.alerte
)

const getTelechargerVisible = computed(
    () => props.carte.secondaire === "telecharger" && props.rapportProposable
)

/** « Voir la recommandation » : retenir le conseil, ou rouvrir la recommandation. */
function onVoirRecommandation(): void {
    if (props.carte.scenarioConseille)
        emit("retenir-scenario", props.carte.scenarioConseille)
    else emit("voir-recommandation")
}

function onChoix(choix: "dernieres" | "autres"): void {
    menuOuvert.value = false
    if (choix === "dernieres") emit("nouvelle-etude")
    else emit("chiffrer-autres")
}
</script>

<template>
    <!-- ================================ -->
    <!-- CARTE DE TÊTE (contrat v1.2 §4) : trois lignes lisibles au repos
         (verdict et montant, raison, action principale). -->
    <!-- ================================ -->
    <BaseCard
        class="optim-turpe-v2-carte-de-tete"
        :pt="{ body: 'p-6 max-sm:p-5' }"
        data-testid="optim-turpe-v2-carte-de-tete"
        :data-etat="carte.etat"
    >
        <template #content>
            <!-- Métadonnées et « Nouvelle étude » (C2, C13, C32). -->
            <div class="flex flex-wrap items-center justify-between gap-2">
                <p
                    v-if="getEnTete"
                    class="text-xs text-surface-500"
                    data-testid="optim-turpe-v2-carte-meta"
                >
                    {{ getEnTete }}
                </p>
                <div
                    v-if="relancable"
                    ref="blocMenu"
                    class="relative"
                    @keydown.esc="menuOuvert = false"
                >
                    <BaseButton
                        v-bind="NIVEAU_2"
                        :label="t('nouvelleEtude')"
                        icon="pi pi-refresh"
                        :aria-expanded="menuOuvert"
                        data-testid="optim-turpe-v2-nouvelle-etude"
                        @click="menuOuvert = !menuOuvert"
                    />
                    <div
                        v-if="menuOuvert"
                        class="absolute right-0 z-10 mt-1 flex w-72 flex-col gap-1 rounded-md border border-surface-200 bg-white p-2 shadow-md"
                        role="menu"
                        data-testid="optim-turpe-v2-nouvelle-etude-choix"
                    >
                        <button
                            type="button"
                            role="menuitem"
                            class="flex flex-col rounded px-2 py-1.5 text-left hover:bg-surface-100"
                            data-testid="optim-turpe-v2-nouvelle-etude-dernieres"
                            @click="onChoix('dernieres')"
                        >
                            <span
                                class="text-sm font-medium text-primary-900"
                                >{{ t("surLesDernieresMesures") }}</span
                            >
                            <span class="text-xs text-surface-500">{{
                                t("surLesDernieresMesuresAide")
                            }}</span>
                        </button>
                        <button
                            type="button"
                            role="menuitem"
                            class="flex flex-col rounded px-2 py-1.5 text-left hover:bg-surface-100"
                            data-testid="optim-turpe-v2-nouvelle-etude-autres"
                            @click="onChoix('autres')"
                        >
                            <span
                                class="text-sm font-medium text-primary-900"
                                >{{ t("chiffrerAutres") }}</span
                            >
                            <span class="text-xs text-surface-500">{{
                                t("chiffrerAutresAide")
                            }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Le verdict et sa raison, sur toute la largeur. -->
            <div
                class="mt-4 flex flex-col gap-2"
                data-testid="optim-turpe-v2-carte-tete"
            >
                <div class="flex flex-col gap-1">
                    <h2
                        class="text-3xl font-bold text-primary-900"
                        data-testid="optim-turpe-v2-carte-titre"
                        :aria-label="texteDuTitre(carte.titre)"
                    >
                        {{ carte.titre.texte }}
                        <!-- N3 : le montant et son unité ne se coupent pas ;
                             signe et couleur sur le montant seul. -->
                        <span
                            v-if="getMontant"
                            class="whitespace-nowrap"
                            data-testid="optim-turpe-v2-carte-montant-unite"
                            ><span
                                :class="classeMontant(carte.titre.montantEur)"
                                data-testid="optim-turpe-v2-carte-montant"
                                >{{ getMontant }}</span
                            >{{ getUniteInsecable }}</span
                        >
                    </h2>
                    <p
                        v-if="carte.titre.soitEnviron"
                        class="text-sm text-surface-700"
                        data-testid="optim-turpe-v2-carte-soit-environ"
                    >
                        <OptimTurpeV2TexteSigne
                            :texte="carte.titre.soitEnviron"
                        />
                    </p>
                </div>

                <p
                    v-if="carte.raison"
                    class="text-lg text-surface-700"
                    data-testid="optim-turpe-v2-carte-raison"
                >
                    <OptimTurpeV2TexteSigne :texte="carte.raison" />
                </p>
            </div>

            <div
                class="mt-4"
                :class="
                    getSansLigne
                        ? 'flex flex-col items-start'
                        : 'grid grid-cols-1 gap-6 border-t border-surface-200 pt-4 xl:grid-cols-12'
                "
                data-testid="optim-turpe-v2-carte-grille"
                :data-sans-ligne="getSansLigne ? '1' : '0'"
            >
                <!-- À gauche : la fiche, puis la seule ligne en couleur. -->
                <div
                    v-if="!getSansLigne"
                    class="flex min-w-0 flex-col gap-4 xl:col-span-7"
                >
                    <dl
                        v-if="getFicheVisible"
                        class="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-[auto_minmax(0,1fr)]"
                        data-testid="optim-turpe-v2-carte-fiche"
                    >
                        <template v-if="carte.fiche.scenario">
                            <dt class="text-surface-500 sm:w-36">
                                {{ t("fiche.scenario") }}
                            </dt>
                            <dd
                                class="flex min-w-0 flex-wrap items-center gap-2 text-surface-800"
                                data-testid="optim-turpe-v2-carte-scenario"
                            >
                                <BaseTag
                                    v-if="carte.fiche.scenario.recommande"
                                    :value="t('recommande')"
                                    severity="primary"
                                    class="shrink-0"
                                />
                                <span class="min-w-0 flex-1 basis-40"
                                    >{{ carte.fiche.scenario.nom
                                    }}<template
                                        v-if="carte.fiche.scenario.detail"
                                        >&nbsp;:
                                        {{
                                            carte.fiche.scenario.detail
                                        }}</template
                                    ></span
                                >
                            </dd>
                        </template>
                        <template v-if="carte.fiche.aDemander">
                            <dt class="text-surface-500 sm:w-36">
                                {{ t("fiche.aDemander") }}
                            </dt>
                            <!-- Contrat v1.2 §4 (R-T10) : les postes par
                                 leurs codes, comme le tableau de la demande. -->
                            <dd
                                class="font-semibold text-primary-900"
                                data-codes-postes
                                data-testid="optim-turpe-v2-carte-a-demander"
                            >
                                {{ carte.fiche.aDemander }}
                            </dd>
                        </template>
                        <template v-if="carte.fiche.pourquoi">
                            <dt class="text-surface-500 sm:w-36">
                                {{ carte.fiche.pourquoi.titre }}
                            </dt>
                            <dd
                                class="text-surface-800"
                                data-testid="optim-turpe-v2-carte-pourquoi"
                            >
                                {{ carte.fiche.pourquoi.texte }}
                            </dd>
                        </template>
                        <template v-if="carte.fiche.composition.length > 0">
                            <dt class="text-surface-500 sm:w-36">
                                {{ t("fiche.dontVient") }}
                            </dt>
                            <dd
                                class="text-surface-800"
                                data-testid="optim-turpe-v2-carte-composition"
                            >
                                <template
                                    v-for="(terme, index) in carte.fiche
                                        .composition"
                                    :key="terme.nom"
                                    ><template v-if="index > 0"> · </template
                                    >{{ terme.nom }}&nbsp;:
                                    <span
                                        :class="classeSegment(terme.sens)"
                                        :data-sens="terme.sens"
                                        >{{ terme.montant }}</span
                                    ><template v-if="terme.precision">{{
                                        ` ${terme.precision}`
                                    }}</template></template
                                >
                            </dd>
                        </template>
                        <!-- C5 sous le pic (DEV-2422) : le montant du
                             chiffrage dédié et sa provenance en mots, puis la
                             formule recalculée dite à part. -->
                        <template v-if="carte.fiche.provenance">
                            <dt class="text-surface-500 sm:w-36">
                                {{ t("fiche.dontVient") }}
                            </dt>
                            <dd
                                class="text-surface-800"
                                data-testid="optim-turpe-v2-carte-provenance"
                            >
                                {{ carte.fiche.provenance
                                }}<template
                                    v-if="carte.fiche.formuleRecalculee"
                                    >{{
                                        ` ${carte.fiche.formuleRecalculee}`
                                    }}</template
                                >
                            </dd>
                        </template>
                        <template v-if="carte.fiche.periode">
                            <dt class="text-surface-500 sm:w-36">
                                {{ t("fiche.periode") }}
                            </dt>
                            <dd
                                class="text-surface-800"
                                data-testid="optim-turpe-v2-carte-periode"
                            >
                                {{ carte.fiche.periode }}
                            </dd>
                        </template>
                    </dl>

                    <!-- Une action avant d'envoyer, au format court (R-T7,
                         A1) ; un constat du raccordement en gris (R-T6). -->
                    <BaseMessage
                        v-if="carte.alerte"
                        :severity="
                            carte.alerte.niveau === 'action'
                                ? 'warn'
                                : 'secondary'
                        "
                        size="small"
                        :icon="
                            carte.alerte.niveau === 'action'
                                ? 'pi pi-exclamation-triangle'
                                : 'pi pi-info-circle'
                        "
                        :pt="{
                            root:
                                carte.alerte.niveau === 'action'
                                    ? 'bg-[var(--p-orange-50)]! opacity-100!'
                                    : 'opacity-100!',
                            text:
                                carte.alerte.niveau === 'action'
                                    ? 'text-[var(--p-orange-700)]! font-medium'
                                    : 'text-surface-700!',
                            transition: { name: 'aucune', appear: false },
                        }"
                        :data-niveau-alerte="carte.alerte.niveau"
                        data-testid="optim-turpe-v2-carte-alerte"
                    >
                        {{ carte.alerte.texte }}
                        <BaseButton
                            v-if="carte.alerte.ancre"
                            v-bind="NIVEAU_3"
                            :label="t('corriger')"
                            data-testid="optim-turpe-v2-carte-corriger"
                            @click="emit('corriger', carte.alerte.ancre)"
                        />
                    </BaseMessage>
                </div>

                <!-- À droite : un niveau 1, un niveau 2 au plus, un lien. -->
                <div
                    v-if="carte.action !== null || getTelechargerVisible"
                    class="flex min-w-0 flex-col items-start gap-3"
                    :class="
                        getSansLigne
                            ? ''
                            : 'xl:col-span-5 xl:border-l xl:border-surface-200 xl:pl-6'
                    "
                    data-testid="optim-turpe-v2-carte-actions"
                >
                    <BaseButton
                        v-if="carte.action === 'preparer_demande'"
                        v-bind="NIVEAU_1"
                        :label="t('preparerDemande')"
                        icon="pi pi-envelope"
                        class="w-fit whitespace-nowrap"
                        data-testid="optim-turpe-v2-carte-preparer-demande"
                        @click="emit('preparer-demande')"
                    />
                    <BaseButton
                        v-else-if="carte.action === 'voir_resume'"
                        v-bind="NIVEAU_1"
                        :label="t('voirResume')"
                        icon="pi pi-align-left"
                        class="w-fit whitespace-nowrap"
                        data-testid="optim-turpe-v2-carte-voir-resume"
                        @click="emit('voir-resume')"
                    />
                    <BaseButton
                        v-else-if="
                            carte.action === 'chiffrer_periode' && carte.periode
                        "
                        v-bind="NIVEAU_1"
                        :label="t('chiffrerCettePeriode')"
                        :aria-label="libelleChiffrerSurLaPeriode(carte.periode)"
                        icon="pi pi-refresh"
                        class="w-fit whitespace-nowrap"
                        data-testid="optim-turpe-v2-carte-chiffrer-periode"
                        @click="emit('chiffrer-periode', carte.periode)"
                    />
                    <BaseButton
                        v-else-if="carte.action === 'modifier_hypotheses'"
                        v-bind="NIVEAU_1"
                        :label="t('modifierHypotheses')"
                        icon="pi pi-sliders-h"
                        class="w-fit whitespace-nowrap"
                        data-testid="optim-turpe-v2-carte-modifier-hypotheses"
                        @click="emit('modifier-hypotheses')"
                    />
                    <BaseButton
                        v-else-if="carte.action === 'voir_recommandation'"
                        v-bind="NIVEAU_1"
                        :label="t('voirRecommandation')"
                        class="w-fit whitespace-nowrap"
                        data-testid="optim-turpe-v2-carte-voir-recommandation"
                        @click="onVoirRecommandation"
                    />

                    <BaseButton
                        v-if="carte.secondaire === 'preparer_demande'"
                        v-bind="NIVEAU_2"
                        :label="t('preparerDemande')"
                        icon="pi pi-envelope"
                        class="w-fit whitespace-nowrap"
                        data-testid="optim-turpe-v2-carte-preparer-demande"
                        @click="emit('preparer-demande')"
                    />
                    <BaseButton
                        v-else-if="carte.secondaire === 'voir_resume'"
                        v-bind="NIVEAU_2"
                        :label="t('voirResume')"
                        icon="pi pi-align-left"
                        class="w-fit whitespace-nowrap"
                        data-testid="optim-turpe-v2-carte-voir-resume"
                        @click="emit('voir-resume')"
                    />
                    <BaseButton
                        v-else-if="getTelechargerVisible"
                        v-bind="NIVEAU_2"
                        :label="t('telecharger')"
                        icon="pi pi-download"
                        class="w-fit whitespace-nowrap"
                        :loading="rapportEnCours"
                        data-testid="optim-turpe-v2-carte-telecharger"
                        @click="emit('telecharger')"
                    />

                    <BaseButton
                        v-if="carte.lien === 'voir_resume'"
                        v-bind="NIVEAU_3"
                        :label="t('voirResume')"
                        data-testid="optim-turpe-v2-carte-voir-resume"
                        @click="emit('voir-resume')"
                    />
                </div>
            </div>

            <!-- La ligne de périmètre, sous un filet, toute la largeur (Q17). -->
            <p
                class="mt-4 border-t border-surface-200 pt-3 text-xs text-surface-500"
                data-testid="optim-turpe-v2-carte-perimetre"
            >
                {{ carte.perimetre }}
            </p>
        </template>
    </BaseCard>
</template>
